import { useState, useEffect } from "react";
import { Phone, Menu, X, Facebook, Instagram } from "lucide-react";
import { TikTokIcon } from "./icons/TikTokIcon";
import { COMPANY } from "../data/site";
import { useEstimate } from "../context/EstimateContext";

const LINKS = [
  { label: "Services", href: "/#services" },
  { label: "Work", href: "/#work" },
  { label: "Why Apex", href: "/#why" },
  { label: "Reviews", href: "/#reviews" },
  { label: "Contact", href: "/#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { openEstimate } = useEstimate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-slate-200 shadow-sm"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[84px] flex items-center justify-between">
        <a href="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <img src="/apex-logo.png" alt="Apex Home Pro Inc logo" className="h-16 w-auto" />
          <span className="font-display font-extrabold text-lg text-[var(--apex-primary)] mt-1">Inc.</span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-slate-600 hover:text-[var(--apex-accent)] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={COMPANY.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="header-facebook" className="text-slate-500 hover:text-[var(--apex-accent)] transition-colors"><Facebook size={18} /></a>
          <a href={COMPANY.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="header-instagram" className="text-slate-500 hover:text-[var(--apex-accent)] transition-colors"><Instagram size={18} /></a>
          <a href={COMPANY.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="header-tiktok" className="text-slate-500 hover:text-[var(--apex-accent)] transition-colors"><TikTokIcon size={18} /></a>
          <span className="h-5 w-px bg-slate-300" />
          <a href={`tel:${COMPANY.phoneRaw}`} data-testid="header-phone" className="flex items-center gap-2 text-sm font-semibold text-[var(--apex-primary)] hover:text-[var(--apex-accent)] transition-colors">
            <Phone size={16} /> {COMPANY.phone}
          </a>
          <button
            data-testid="header-estimate-btn"
            onClick={openEstimate}
            className="rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white text-sm font-semibold px-5 py-2.5 transition-colors"
          >
            Get Free Estimate
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[var(--apex-primary)]"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-3">
          {LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="block text-slate-700 font-medium py-1">
              {l.label}
            </a>
          ))}
          <button
            data-testid="mobile-estimate-btn"
            onClick={() => { setMenuOpen(false); openEstimate(); }}
            className="w-full rounded-sm bg-[var(--apex-accent)] text-white font-semibold px-5 py-3 mt-2"
          >
            Get Free Estimate
          </button>
        </div>
      )}
    </header>
  );
};
